import { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useSearchParameter } from '@xing-com/crate-hooks-use-search-parameter';
import {
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_ALERTS_MODAL_TYPES,
} from '@xing-com/crate-jobs-constants';
import {
  useToggleSearchAlert,
  useSearchAlertRequiredFields,
} from '@xing-com/crate-jobs-hooks';
import {
  IconSearchAlertActivated,
  IconNotificationCenter,
  IconSave,
} from '@xing-com/icons';
import { mediaConfined, zIndexStickyLayer3 } from '@xing-com/layout-tokens';

const CSS_VARIABLES = {
  duration: '--duration',
  easing: '--easing',
  inlinePadding: '--inline-padding',
  justifyContent: '--justify-content',
};

type Props = {
  isStickyHeader: boolean;
  empty: boolean;
};

type GetCopyArgs = {
  isAlertActive: boolean;
  isLoggedIn: boolean;
  isEditMode: boolean;
};
const getCopy = ({
  isAlertActive,
  isLoggedIn,
  isEditMode,
}: GetCopyArgs): string => {
  if (!isLoggedIn) {
    return 'JOBS_SEARCH_ALERT_LO_BANNER_ACTIVATE';
  }
  if (isAlertActive) {
    return 'JOBS_SEARCH_ALERT_CREATED';
  }

  return isEditMode
    ? 'JOBS_SEARCH_EDIT_ALERT_BANNER_TEXT'
    : 'JOBS_CREATE_SEARCH_ALERT';
};

type GetIconArgs = { isAlertActive: boolean; isEditMode: boolean };
const getIcon = ({
  isAlertActive,
  isEditMode,
}: GetIconArgs): React.FC<React.SVGProps<SVGSVGElement>> => {
  if (isAlertActive) {
    return IconSearchAlertActivated;
  }

  return isEditMode ? IconSave : IconNotificationCenter;
};

export const Container: React.FC<Props> = ({ isStickyHeader, empty }) => {
  const { isLoggedIn } = useLoginState();
  const { setSearchParameter } = useSearchParameter();
  const [width, setWidth] = useState(0);
  const ref = useRef<HTMLButtonElement>(null);

  const { keywords, location } = useSearchAlertRequiredFields();
  const { isAlertActive, isEditMode, onToggleAlert } = useToggleSearchAlert();

  useEffect(() => {
    setWidth(ref?.current?.getBoundingClientRect().width ?? 0);
  }, [isStickyHeader, isAlertActive]);

  // Do not show on empty results or when there's no keywords or location
  if (empty || (!keywords && !location)) {
    return null;
  }

  const handleOnClick = (): void => {
    if (isLoggedIn) {
      onToggleAlert({
        PropTrackActionData: {
          PropActionOrigin: isAlertActive
            ? 'jobs_toggel_off'
            : 'jobs_toggel_on',
        },
        nwtTrackingOptions: {
          elementDetail: 'standard_search',
        },
      });
      return;
    }

    setSearchParameter(
      SEARCH_ALERTS_MODAL_PARAM,
      SEARCH_ALERTS_MODAL_TYPES.CREATE
    );
  };

  // Since we need to know the initial width of the element, we initially render
  // it without modifying it's width. Once we know the "default" element's
  // width, we can now safely set a fixed width (which initially and when not
  // scrolled will equal to the default)
  const collapseButton =
    isStickyHeader && (!isEditMode || (isEditMode && isAlertActive));
  const style = ref.current
    ? {
        [CSS_VARIABLES.inlinePadding]: `${collapseButton ? 10 : 16}px`,
        [CSS_VARIABLES.justifyContent]: collapseButton
          ? 'flex-start'
          : 'center',
        width: `${collapseButton ? 42 : width}px`,
      }
    : {};

  const icon = getIcon({ isAlertActive, isEditMode });
  const copy = getCopy({ isAlertActive, isLoggedIn, isEditMode });

  return (
    <>
      <HiddenAlertButton
        ref={ref}
        tabIndex={-1}
        icon={icon}
        variant="tertiary"
        size={isStickyHeader ? 'medium' : 'small'}
        aria-hidden
      >
        <FormattedMessage id={copy} />
      </HiddenAlertButton>

      <AlertButton
        variant="primary"
        size={isStickyHeader ? 'medium' : 'small'}
        style={style}
        $isStickyHeader={isStickyHeader}
        icon={icon}
        onClick={handleOnClick}
      >
        <FormattedMessage id={copy} />
      </AlertButton>
    </>
  );
};

// We render a hidden unusable button so that we can detect the actual button's
// width. The reason why we cannot use the original button is because there are
// some cases in which the width is not properly calculated and then, when
// isStickyHeader is false, the button does not actually extend to the expected
// width
const HiddenAlertButton = styled(Button)`
  z-index: 0;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
`;

const AlertButton = styled(Button)<{ $isStickyHeader: boolean }>`
  z-index: ${zIndexStickyLayer3};
  overflow: hidden;
  position: ${({ $isStickyHeader }) =>
    $isStickyHeader ? 'fixed' : 'relative'};
  flex-shrink: 0;
  margin-left: 20px;
  right: ${({ $isStickyHeader }) => ($isStickyHeader ? 20 : 0)}px;
  top: ${({ $isStickyHeader }) => ($isStickyHeader ? 126 : 0)}px;
  padding-inline: var(${CSS_VARIABLES.inlinePadding});

  ${CSS_VARIABLES.easing}: cubic-bezier(0.5, 1, 0.75, 1);
  ${CSS_VARIABLES.duration}: 0.2s;
  transition:
    width var(${CSS_VARIABLES.duration}) var(${CSS_VARIABLES.easing}),
    height var(${CSS_VARIABLES.duration}) var(${CSS_VARIABLES.easing}),
    font-size var(${CSS_VARIABLES.duration}) var(${CSS_VARIABLES.easing}),
    padding-inline var(${CSS_VARIABLES.duration}) var(${CSS_VARIABLES.easing});

  & svg {
    /*
      To make the animation smoother, we are adding a transition
      to the changes in the SVG icon. Only the width and height are affected,
      as they are the only properties that change when the button's size prop is updated.
    */
    transition:
      width var(${CSS_VARIABLES.duration}) var(${CSS_VARIABLES.easing}),
      height var(${CSS_VARIABLES.duration}) var(${CSS_VARIABLES.easing});
  }

  display: flex;
  justify-content: var(${CSS_VARIABLES.justifyContent});
  & div {
    justify-content: flex-start;
  }

  & span:last-child {
    flex-shrink: 0;
  }

  @media ${mediaConfined} {
    display: none;
  }
`;
